import React, { createContext, useContext, useState, useCallback } from 'react';
import { apiService } from '../services/api';
import type { MinimalDocument } from '../types';
import type {
  DocumentSelectionState,
  DocumentSelectionContextValue,
  SelectionSource,
} from '../types/documentSelection';
import type { MentionFilters } from '../components/chat/MentionTypes';
import logger from '../utils/simpleLogger';

const DocumentSelectionContext = createContext<DocumentSelectionContextValue | null>(null);

export function DocumentSelectionProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<DocumentSelectionState>({
    selectedIds: [],
    documents: new Map<string, MinimalDocument>(),
    filters: {},
    sources: new Map<string, SelectionSource>(),
    availableDocumentIds: new Set<string>(),
  });

  const selectDocuments = useCallback(
    (documentIds: string[], source: SelectionSource = 'manual') => {
      setState((prev) => {
        const newDocuments = new Map(prev.documents);
        const newSources = new Map(prev.sources);
        const newSelectedIds = [...prev.selectedIds];

        documentIds.forEach((id) => {
          if (!newSelectedIds.includes(id)) {
            newSelectedIds.push(id);
            newSources.set(id, source);
          }
        });

        return {
          ...prev,
          selectedIds: newSelectedIds,
          sources: newSources,
          documents: newDocuments,
        };
      });
    },
    []
  );

  const deselectDocuments = useCallback((documentIds: string[]) => {
    setState((prev) => {
      const newSelectedIds = prev.selectedIds.filter((id) => !documentIds.includes(id));
      const newDocuments = new Map(prev.documents);
      const newSources = new Map(prev.sources);

      documentIds.forEach((id) => {
        newSources.delete(id);
      });

      return {
        ...prev,
        selectedIds: newSelectedIds,
        sources: newSources,
        documents: newDocuments,
      };
    });
  }, []);

  const setFilters = useCallback((filters: MentionFilters) => {
    setState((prev) => ({
      ...prev,
      filters,
    }));
  }, []);

  const loadDocument = useCallback(
    async (documentId: string) => {
      // Check if document is already loaded
      if (state.documents.has(documentId)) {
        return;
      }

      // Check if document ID is in available list
      if (state.availableDocumentIds.size > 0 && !state.availableDocumentIds.has(documentId)) {
        logger.warn('📄 [DocumentSelection] Document ID not in available list', { documentId });
        return;
      }

      try {
        const response = await apiService.getDocument(documentId);
        if (response.document) {
          const doc: MinimalDocument = {
            id: response.document.id,
            name:
              response.document.name ||
              response.document.original_filename ||
              'Documento sin nombre',
            documentType: response.document.file_type || 'other',
            scope: (response.document.scope as 'private' | 'shared') || 'private',
            original_filename: response.document.original_filename,
            company_name: response.document.company_name,
            document_type: response.document.document_type,
            document_period: response.document.document_period,
          };

          setState((prev) => {
            const newDocuments = new Map(prev.documents);
            newDocuments.set(documentId, doc);
            return {
              ...prev,
              documents: newDocuments,
            };
          });
        }
      } catch (error) {
        // Handle 404 errors gracefully - don't log them as they're expected when documents are deleted
        if (error instanceof Error && error.message.includes('Document not found')) {
          logger.debug('📄 [DocumentSelection] Document not found (expected)', { documentId });
        } else {
          logger.error('📄 [DocumentSelection] Error loading document', { documentId, error });
        }
      }
    },
    [state.documents, state.availableDocumentIds]
  );

  const clearSelection = useCallback(() => {
    setState((prev) => ({
      ...prev,
      selectedIds: [],
      sources: new Map(),
    }));
  }, []);

  const setDocument = useCallback((document: MinimalDocument) => {
    setState((prev) => {
      const newDocuments = new Map(prev.documents);
      newDocuments.set(document.id, document);
      return {
        ...prev,
        documents: newDocuments,
      };
    });
  }, []);

  const setAvailableDocumentIds = useCallback((documentIds: string[]) => {
    setState((prev) => ({
      ...prev,
      availableDocumentIds: new Set(documentIds),
    }));
  }, []);

  const value: DocumentSelectionContextValue = {
    state,
    selectDocuments,
    deselectDocuments,
    setFilters,
    loadDocument,
    clearSelection,
    setDocument,
    setAvailableDocumentIds,
  };

  return (
    <DocumentSelectionContext.Provider value={value}>{children}</DocumentSelectionContext.Provider>
  );
}

export function useDocumentSelectionContext() {
  const ctx = useContext(DocumentSelectionContext);
  if (!ctx) {
    throw new Error(
      'useDocumentSelectionContext must be used inside <DocumentSelectionProvider />'
    );
  }
  return ctx;
}

// Re-export MinimalDocument type for convenience
export type { MinimalDocument } from '../types';
